import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import UserContext from "./UserContext";

// Decode JWT exp claim without verifying signature (client-side expiry check only).
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp && Date.now() / 1000 > payload.exp;
  } catch {
    return true;
  }
}

// requiredRole: "patient" | "clinician" | undefined (any authenticated user)
function ProtectedRoute({ children, requiredRole }) {
  const { user, setUser } = useContext(UserContext);
  const location = useLocation();

  if (!user || !user.token) {
    return <Navigate to="/" replace />;
  }

  if (isTokenExpired(user.token)) {
    setUser(null);
    return <Navigate to="/" replace />;
  }

  // Enforce first-login password change for every protected page except /change-password itself
  if (user.force_password_change && location.pathname !== "/change-password") {
    return <Navigate to="/change-password?forced=true" replace />;
  }

  // 2026-10-23 addition (clinician invite-only build, Piece 4) — an
  // invited clinician whose identity (name/practice/NPI/signature) isn't
  // on file yet must complete that gate before reaching any other
  // clinician-only page, same redirect pattern as force_password_change
  // above (checked second, so a brand-new invited clinician clears their
  // forced password change first, then lands here next). /complete-
  // profile is self-exempted the same way /change-password is above.
  // 2026-10-26 fix (confirmed real bug, live-reproduced — Chrome's
  // "Throttling navigation to prevent the browser from hanging") — a
  // freshly invited clinician has force_password_change AND
  // identity_profile_incomplete both true at once. Before this fix, this
  // check had no exemption for /change-password, so it fired while the
  // user was still sitting on that page (that page is only exempted from
  // the check ABOVE, not this one) and sent them to /complete-profile —
  // which then immediately bounced back to /change-password via the
  // check above (force_password_change still true there), forever.
  // Exempting /change-password here too lets the force_password_change
  // check fully own routing until the password is actually changed, then
  // this check correctly takes over on the next render.
  if (
    user.identity_profile_incomplete &&
    location.pathname !== "/complete-profile" &&
    location.pathname !== "/change-password"
  ) {
    return <Navigate to="/complete-profile" replace />;
  }

  // Role gate: patients cannot access clinician routes and vice versa
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/home" replace />;
  }

  return children;
}

export default ProtectedRoute;
